/* eslint-disable max-lines */
import { DashboardConfig, UUID } from '@litlingo/client';
import { setActiveDashboard, setTimeRange } from 'actions/dashboard';
import { fetchFilterRules } from 'actions/ruleGroup';
import BackendResourceFilter from 'components/Filters/BackendResourceFilter';
import BackendResourceFilterDashboard from 'components/Filters/BackendResourceFilter/BackendResourceFilterDashboard';
import OutOfRetentionFilter from 'components/Filters/OutOfRetention';
import ResourceFilter from 'components/Filters/ResourceFilter';
import CategoriesFilter from 'components/Filters/SidebarTypeFilters/CategoriesFilter/CategoriesFilter';
import DashboardDateFilter from 'components/Filters/SidebarTypeFilters/DashboardDateFilter';
import SelectSidebar from 'components/SelectSidebar';
import { replace } from 'connected-react-router';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import DashboardFiltersTreeContext, {
  DashboardFiltersTreeProvider,
} from 'contexts/DashboardFiltersTreeContext';
import useAddDashboardEditTreeToStore from 'hooks/envelopes/useAddDashboardTreeToStore';
import useTeamsForFilters from 'hooks/teams/useTeamsForFilters';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { generatePath, useParams } from 'react-router-dom';
import {
  getDashboardState,
  getNavParamsFromDashboardTree,
  getSidebarState,
} from 'selectors/dashboard';
import { getNavParamsByResourceMemo, getNavParamsCreate } from 'selectors/nav';
import { getTagsTotalCount } from 'selectors/tags';
import { getTeamsCount, getTeamsLoading, getTeamsWithCounts } from 'selectors/teams';
import { getUsersLoading, getUsersNoSuperAdminList, getUsersTotalCount } from 'selectors/users';
import { useSelector } from 'store';
import {
  clearURLParams,
  fetchFilterModels,
  fetchManagementLevelsForFilter,
  fetchRegionsForFilter,
  fetchSendersForFilter,
  fetchTeamsForFilter,
  setURLParams,
} from '../../actions';
import { dashboardConfigFiltersMap } from '../../constants/dashboard';
import logEvent from '../../utils/analytics';
import SidebarCollapsableSection from '../CollapsableSection/SidebarCollapsableSection';
import ExportFilter from './ExportFilter';
import TimeRangeFilterRedesign from './Filters/TimeRangeFilterRedesign';
import UnitOfTimeFilter from './Filters/UnitOfTimeFilter';

type ComponentProps = {
  resource: string;
  params: { [paramName: string]: string | undefined };
  dashboardConfig: DashboardConfig | null;
};
const EventsFiltersSidebar: React.FC<ComponentProps> = ({ resource, params, dashboardConfig }) => {
  const dispatch = useDispatch();
  const { activeUuid } = useParams<{ activeUuid: string }>();
  const { is_exporting: isExporting } = useSelector(getNavParamsCreate);
  const dashboardState = useSelector(getDashboardState(activeUuid));

  const { data: teamsData } = useTeamsForFilters({
    kind: 'division',
  });
  const { data: regionsData } = useTeamsForFilters({
    kind: 'region',
  });
  const { data: managementLevelData } = useTeamsForFilters({
    kind: 'management-level',
  });

  const treeParams = useSelector(getNavParamsFromDashboardTree);
  const navParams = useSelector((state) =>
    getNavParamsByResourceMemo(state, resourceQueryParamName.metrics)
  );
  useAddDashboardEditTreeToStore(navParams.filters_search as string);

  const { widgets } = dashboardState;

  const {
    loadedDashboards,
    customerDomain,
    params: statefulNavParams,
  } = useSelector(getSidebarState(resource));

  useEffect(() => {
    if (params?.activeUuid) {
      dispatch(setActiveDashboard(params?.activeUuid));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectOptions: { [key: string]: string } = { default: 'Default' };
  loadedDashboards?.forEach((item) => {
    selectOptions[item.uuid] = item.name;
  });

  const filteredParams = Object.entries(params).filter(
    ([key]) => !['offset', 'limit', 'order_desc', 'order_by', 'has_events'].includes(key)
  );

  const handleResetFiltersClick = (): void => {
    logEvent('events-filters-reset-filters');
    dispatch(clearURLParams());
    dispatch(setTimeRange({ after: '', before: '' }));
  };

  const handleSetActiveDashboard = (value: UUID | undefined): void => {
    if (value !== 'default') {
      const newPath = generatePath('/:customerDomain/dashboards/:activeUuid', {
        customerDomain: customerDomain as string,
        activeUuid: value as string,
      });

      dispatch(replace(newPath));
    } else {
      const newPath = generatePath('/:customerDomain', {
        customerDomain: customerDomain as string,
      });

      dispatch(replace(newPath));
    }
    dispatch(setActiveDashboard(value));
  };

  const handleExport = (): void => {
    dispatch(setURLParams({ is_exporting: 'true' }));
  };

  const canExportWidgets = widgets.some((w) => !!w.export_label);

  return (
    <DashboardFiltersTreeProvider>
      <div>
        <div id="envelope-header-div" className="bg-litlingo-primary-140">
          <div className="items-center flex flex-row justify-end">
            <button
              type="button"
              className={`underline focus:outline-none ${
                !(filteredParams.length > 0) && 'text-litlingo-gray-4 cursor-default'
              }`}
              onClick={handleResetFiltersClick}
              disabled={!(filteredParams.length > 0)}
            >
              Clear All
            </button>
          </div>
        </div>
        <div className="flex flex-col relative z-20">
          <div className="flex flex-col mt-4">
            {loadedDashboards != null && loadedDashboards.length > 0 && isExporting !== 'true' && (
              <div className="border-b border-litlingo-white pb-3">
                <SelectSidebar
                  placeholder="Dashboards"
                  onChange={handleSetActiveDashboard}
                  options={Object.keys(selectOptions).map((key) => ({
                    label: selectOptions[key],
                    value: key,
                  }))}
                  value={params?.activeUuid}
                />
              </div>
            )}
            {dashboardConfig?.filters != null &&
              dashboardConfig?.filters.map((filter) => {
                if (filter.dimension === 'categories') {
                  return (
                    <SidebarCollapsableSection
                      key={`${filter.type}.models`}
                      headerTitle={filter.all_label || ''}
                      isDefaultClosed
                      isOpen={
                        !!(treeParams && (treeParams.campaign_uuids || treeParams.rule_uuids))
                      }
                      selectedFilters={
                        treeParams && (treeParams.campaign_uuids || treeParams.rule_uuids)
                      }
                    >
                      <div className="flex flex-col gap-3">
                        <CategoriesFilter
                          campaignsKey="campaign_uuids"
                          rulesKey="rule_uuids"
                          title="Models"
                          fetchCampaigns={fetchFilterModels}
                          fetchRules={fetchFilterRules}
                          treeContext={DashboardFiltersTreeContext}
                        />
                      </div>
                    </SidebarCollapsableSection>
                  );
                }

                const spec = dashboardConfigFiltersMap[filter.dimension];

                switch (filter.type) {
                  case 'date':
                    return (
                      <SidebarCollapsableSection
                        key={`${filter.type}.${filter.dimension}`}
                        headerTitle="Date"
                        isDefaultClosed
                        isOpen
                        selectedFilters={statefulNavParams && statefulNavParams[filter.dimension]}
                      >
                        <DashboardDateFilter key={`${filter.type}.${filter.dimension}`} />
                      </SidebarCollapsableSection>
                    );
                  case 'date_range_select':
                    return <TimeRangeFilterRedesign key={`${filter.type}.${filter.dimension}`} />;
                  case 'unit_of_time_select':
                    return <UnitOfTimeFilter key={`${filter.type}.${filter.dimension}`} />;
                  case 'dropdown':
                    switch (filter.dimension) {
                      case 'user':
                        return (
                          <SidebarCollapsableSection
                            key={`${filter.type}.${filter.dimension}`}
                            headerTitle={filter.all_label || ''}
                            isDefaultClosed
                            isOpen={!!(treeParams && treeParams.created_by)}
                            selectedFilters={treeParams && treeParams.created_by}
                          >
                            <BackendResourceFilter
                              resource={resource}
                              fetchAction={fetchSendersForFilter}
                              getResourceList={getUsersNoSuperAdminList}
                              getResourceCount={getUsersTotalCount}
                              getLoading={getUsersLoading}
                              filterKey="created_by"
                              title={filter.all_label}
                              nameKey="name"
                              secondNameKey="email"
                              dataTestid="user-filter-button"
                              allowExclude
                              treeContext={DashboardFiltersTreeContext}
                            />
                          </SidebarCollapsableSection>
                        );
                      case 'tag':
                        return (
                          <SidebarCollapsableSection
                            key={`${filter.type}.${filter.dimension}`}
                            headerTitle={filter.all_label || ''}
                            isDefaultClosed
                            isOpen={!!(statefulNavParams && statefulNavParams[filter.dimension])}
                            selectedFilters={
                              statefulNavParams && statefulNavParams[filter.dimension]
                            }
                          >
                            <BackendResourceFilterDashboard
                              resource={resource}
                              fetchAction={spec.fetchDataAction}
                              fetchLabelsResourceList={spec.fetchDataAction}
                              getResourceCount={getTagsTotalCount}
                              // @ts-ignore
                              getResourceList={spec.getDataSelector}
                              getLabelsResourceList={spec.getDataSelector}
                              // @ts-ignore
                              getLoading={spec.getDataLoading}
                              filterKey={filter.dimension}
                              title={filter.all_label}
                              // @ts-ignore
                              nameKey={filter.dimension === 'tag' ? 'value' : 'name'}
                              allowExclude={false}
                              dropdown
                            />
                          </SidebarCollapsableSection>
                        );
                      case 'team':
                        return teamsData?.count && teamsData?.count > 0 ? (
                          <SidebarCollapsableSection
                            key={`${filter.type}.${filter.dimension}`}
                            headerTitle={filter.all_label || ''}
                            isDefaultClosed
                            isOpen={!!(treeParams && treeParams.sender_team_uuids)}
                            selectedFilters={treeParams && treeParams.sender_team_uuids}
                          >
                            <BackendResourceFilter
                              resource={resource}
                              fetchAction={fetchTeamsForFilter}
                              getResourceCount={getTeamsCount}
                              getResourceList={getTeamsWithCounts}
                              getLoading={getTeamsLoading}
                              filterKey="sender_team_uuids"
                              title="Division"
                              nameKey="name"
                              filterCountsField="teams"
                              allowExclude
                              treeContext={DashboardFiltersTreeContext}
                            />
                          </SidebarCollapsableSection>
                        ) : null;
                      case 'region':
                        return regionsData?.count && regionsData?.count > 0 ? (
                          <SidebarCollapsableSection
                            key={`${filter.type}.${filter.dimension}`}
                            headerTitle={filter.all_label || ''}
                            isDefaultClosed
                            isOpen={!!(treeParams && treeParams.sender_regions)}
                            selectedFilters={treeParams && treeParams.sender_regions}
                          >
                            <BackendResourceFilter
                              resource={resource}
                              fetchAction={fetchRegionsForFilter}
                              getResourceCount={getTeamsCount}
                              getResourceList={getTeamsWithCounts}
                              getLoading={getTeamsLoading}
                              filterKey="sender_regions"
                              title="Region"
                              nameKey="name"
                              allowExclude
                              filterCountsField="teams"
                              treeContext={DashboardFiltersTreeContext}
                            />
                          </SidebarCollapsableSection>
                        ) : null;
                      case 'management_level':
                        return managementLevelData?.count && managementLevelData?.count > 0 ? (
                          <SidebarCollapsableSection
                            key={`${filter.type}.${filter.dimension}`}
                            headerTitle={filter.all_label || ''}
                            isDefaultClosed
                            isOpen={!!(treeParams && treeParams.sender_management_levels)}
                            selectedFilters={treeParams && treeParams.sender_management_levels}
                          >
                            <BackendResourceFilter
                              resource={resource}
                              fetchAction={fetchManagementLevelsForFilter}
                              getResourceCount={getTeamsCount}
                              getResourceList={getTeamsWithCounts}
                              getLoading={getTeamsLoading}
                              filterKey="sender_management_levels"
                              title="Management Level"
                              nameKey="name"
                              allowExclude
                              filterCountsField="teams"
                              treeContext={DashboardFiltersTreeContext}
                            />
                          </SidebarCollapsableSection>
                        ) : null;
                      case 'retention':
                        return (
                          <SidebarCollapsableSection
                            key={`${filter.type}.${filter.dimension}`}
                            headerTitle={filter.all_label || ''}
                            isDefaultClosed
                            isOpen={!!(statefulNavParams && statefulNavParams[filter.dimension])}
                            selectedFilters={
                              statefulNavParams && statefulNavParams[filter.dimension]
                            }
                          >
                            <OutOfRetentionFilter />
                          </SidebarCollapsableSection>
                        );
                      case 'review_value':
                        return (
                          <SidebarCollapsableSection
                            key={`${filter.type}.${filter.dimension}`}
                            headerTitle={filter.all_label || ''}
                            isDefaultClosed
                            isOpen={!!(treeParams && treeParams.review_values)}
                            selectedFilters={treeParams && treeParams.review_values}
                          >
                            <ResourceFilter
                              fetchAllAction={spec.fetchDataAction}
                              getResourceList={spec.getDataSelector}
                              getLoading={spec.getDataLoading}
                              filterKey="review_values"
                              title={filter.all_label}
                              nameKey="name"
                              allowExclude={false}
                              dropdown
                              treeContext={DashboardFiltersTreeContext}
                            />
                          </SidebarCollapsableSection>
                        );
                      default:
                        return (
                          <SidebarCollapsableSection
                            key={`${filter.type}.${filter.dimension}`}
                            headerTitle={filter.all_label || ''}
                            isDefaultClosed
                            isOpen={!!(treeParams && treeParams[filter.dimension])}
                            selectedFilters={treeParams && treeParams[filter.dimension]}
                          >
                            <ResourceFilter
                              fetchAllAction={spec.fetchDataAction}
                              getResourceList={spec.getDataSelector}
                              getLoading={spec.getDataLoading}
                              filterKey={filter.dimension}
                              title={filter.all_label}
                              nameKey="name"
                              allowExclude={false}
                              dropdown
                              treeContext={DashboardFiltersTreeContext}
                            />
                          </SidebarCollapsableSection>
                        );
                    }

                  default:
                    return <React.Fragment key="fragment" />;
                }
              })}
            {canExportWidgets && isExporting !== 'true' && (
              <button
                type="button"
                className="mt-10 flex self-end button button--tertiary justify-center h-8 w-30 whitespace-no-wrap"
                onClick={handleExport}
              >
                Export Charts
              </button>
            )}
            {isExporting === 'true' && <ExportFilter />}
          </div>
        </div>
      </div>
    </DashboardFiltersTreeProvider>
  );
};

export default EventsFiltersSidebar;
